import type { HiringProcessTemplateFormValues } from "./schema";

type Stages = HiringProcessTemplateFormValues["stages"];

/**
 * Find items that list any of `removedRefs` as a predecessor ("Depends on")
 * and are NOT themselves part of the removed set. Returns their display
 * titles, deduped, in document order.
 *
 * Used to block removing a step/stage while other steps still depend on it —
 * otherwise the save sends a predecessor ref pointing at a step that no longer
 * exists, which the server rejects.
 */
export function findDependentItemTitles(
  stages: Stages,
  removedRefs: Set<string>,
): string[] {
  const titles: string[] = [];
  stages.forEach((stage, sIdx) => {
    stage.items.forEach((item, iIdx) => {
      // Items being removed themselves can't block their own removal.
      if (item.ref && removedRefs.has(item.ref)) {
        return;
      }
      const dependsOnRemoved = (item.predecessors ?? []).some((p) =>
        removedRefs.has(p.ref),
      );
      if (dependsOnRemoved) {
        titles.push(item.title?.trim() || `Item ${iIdx + 1}`);
      }
    });
  });
  return Array.from(new Set(titles));
}

/** Human-readable list of dependent step titles for an error message. */
export function describeDependents(titles: string[]): string {
  const quoted = titles.map((t) => `“${t}”`);
  if (quoted.length === 1) {
    return quoted[0];
  }
  if (quoted.length === 2) {
    return `${quoted[0]} and ${quoted[1]}`;
  }
  const shown = quoted.slice(0, 2).join(", ");
  return `${shown}, and ${quoted.length - 2} other${
    quoted.length - 2 === 1 ? "" : "s"
  }`;
}
